import { useEffect, useState } from 'react';
import { api } from '../api';
import { UiNode } from '../lib/UiNode';
import { IconSparkle, IconChat, IconCheck } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import { CardDetail } from '../components/CardDetail';
import type { BoardDetail, Cluster, IdeaWithReactions, User } from '../../shared/types';

interface PriorityRow {
  userId: string;
  clusterId: string;
  weight: number;
  rank: number | null;
  lockedAt: number | null;
}

export function ClusterPhase({
  board,
  ideas,
  clusters,
  user,
  onClustersChange,
}: {
  board: BoardDetail;
  ideas: IdeaWithReactions[];
  clusters: Cluster[];
  user: User;
  onClustersChange: (next: Cluster[] | ((cur: Cluster[]) => Cluster[])) => void;
}) {
  const [priorities, setPriorities] = useState<PriorityRow[]>([]);
  const [savingPriorities, setSavingPriorities] = useState(false);
  const [openDetail, setOpenDetail] = useState<Cluster | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listPriorities(board.id)
      .then((r) => {
        if (!cancelled) setPriorities(r.priorities);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [board.id]);

  const myPriorities = priorities.filter((p) => p.userId === user.id);
  const isLocked = myPriorities.some((p) => p.lockedAt);

  function myWeight(clusterId: string): number {
    return myPriorities.find((p) => p.clusterId === clusterId)?.weight ?? 50;
  }

  async function setMyWeight(clusterId: string, weight: number): Promise<void> {
    setPriorities((cur) => {
      const others = cur.filter((p) => !(p.userId === user.id && p.clusterId === clusterId));
      return [...others, { userId: user.id, clusterId, weight, rank: null, lockedAt: null }];
    });
    try {
      await api.setPriorities(board.id, { priorities: [{ clusterId, weight }] });
    } catch (e) {
      console.error(e);
    }
  }

  async function lockMyPriorities(): Promise<void> {
    if (savingPriorities) return;
    setSavingPriorities(true);
    try {
      const all = clusters.map((c) => ({
        clusterId: c.id,
        weight: myWeight(c.id),
      }));
      await api.setPriorities(board.id, { priorities: all, lock: true });
      const r = await api.listPriorities(board.id);
      setPriorities(r.priorities);
    } finally {
      setSavingPriorities(false);
    }
  }

  function aggregateScore(clusterId: string): number | null {
    const ps = priorities.filter((p) => p.clusterId === clusterId);
    if (ps.length === 0) return null;
    return Math.round(ps.reduce((s, p) => s + p.weight, 0) / ps.length);
  }

  const [thinking, setThinking] = useState(false);
  const [suggestions, setSuggestions] = useState<
    Array<{ title: string; ideaIds: string[] }> | null
  >(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-cream-3">
          Group ideas that belong together. We'll suggest a start.
        </p>
        <UiNode
          as="button"
          name="cluster-suggest-button"
          entity="cluster"
          action="suggest"
          state={thinking ? 'busy' : 'idle'}
          className="btn inline-flex items-center gap-2"
          disabled={thinking || ideas.length < 2}
          onClick={async () => {
            setThinking(true);
            try {
              const r = await api.suggestClusters(board.id);
              setSuggestions(r.suggestions);
            } finally {
              setThinking(false);
            }
          }}
        >
          <IconSparkle size={14} />
          {thinking ? 'Thinking…' : 'Suggest clusters'}
        </UiNode>
      </div>

      {suggestions && (
        <div className="panel-elev flex flex-col gap-3 p-4">
          <h3 className="text-sm font-medium">Suggested groups</h3>
          <ol className="flex flex-col gap-2">
            {suggestions.map((s, i) => (
              <li key={i} className="text-sm">
                <span className="chip mr-2">{s.title}</span>
                <span className="text-cream-3">{s.ideaIds.length} ideas</span>
              </li>
            ))}
          </ol>
          <div className="flex justify-end gap-2">
            <UiNode
              as="button"
              name="cluster-discard-button"
              entity="cluster"
              action="discard_suggestions"
              className="btn btn-ghost"
              onClick={() => setSuggestions(null)}
            >
              Discard
            </UiNode>
            <UiNode
              as="button"
              name="cluster-apply-button"
              entity="cluster"
              action="apply_suggestions"
              className="btn btn-primary"
              onClick={async () => {
                await api.applyClusters(board.id, suggestions);
                const r = await api.clusters(board.id);
                onClustersChange(r.clusters);
                setSuggestions(null);
              }}
            >
              Apply
            </UiNode>
          </div>
        </div>
      )}

      {clusters.length > 0 && (
        <section className="panel p-4">
          <header className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="text-sm font-medium">Align</h2>
              <p className="text-xs text-cream-3">
                For each theme: skip it, take it or leave it, or it&rsquo;s a must.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-cream-3">
              {isLocked ? (
                <span className="inline-flex items-center gap-1 text-leaf">
                  <IconCheck size={12} /> Locked
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={lockMyPriorities}
                  disabled={savingPriorities}
                >
                  {savingPriorities ? 'Locking…' : 'Lock my priorities'}
                </button>
              )}
            </div>
          </header>
          <ul className="flex flex-col gap-2">
            {clusters.map((c) => {
              const agg = aggregateScore(c.id);
              const stakeholders = priorities.filter((p) => p.clusterId === c.id);
              return (
                <li key={c.id} className="rounded-lg border border-ink-rim/60 bg-ink-3 p-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className="flex-1 text-sm"
                      style={{ color: `hsl(${c.colorHue} 65% 70%)` }}
                    >
                      {c.title}
                    </span>
                    <FeelPicker
                      value={myWeight(c.id)}
                      disabled={isLocked}
                      onChange={(w) => void setMyWeight(c.id, w)}
                    />
                  </div>
                  {stakeholders.length > 1 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-cream-3">
                      <span>Group: {agg == null ? '—' : weightToFeel(agg)}</span>
                      <div className="flex -space-x-1">
                        {stakeholders.map((p) => {
                          const m = board.members.find((x) => x.id === p.userId);
                          if (!m) return null;
                          return (
                            <span key={p.userId} title={`${m.name}: ${weightToFeel(p.weight)}`}>
                              <Avatar user={m} size={16} />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {clusters.map((c) => {
          const ideasIn = ideas.filter((i) => i.clusterId === c.id);
          return (
            <section
              key={c.id}
              className="panel p-4"
              style={{ borderLeft: `3px solid hsl(${c.colorHue} 65% 55%)` }}
            >
              <div className="flex items-center justify-between">
                <h3
                  className="text-sm font-medium"
                  style={{ color: `hsl(${c.colorHue} 65% 70%)` }}
                >
                  {c.title}
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="btn btn-ghost p-1"
                    onClick={() => setOpenDetail(c)}
                    title="Open discussion"
                  >
                    <IconChat size={12} />
                  </button>
                  <span className="text-xs text-cream-3">{ideasIn.length}</span>
                </div>
              </div>
              <ul className="mt-2 flex flex-col gap-1.5 text-sm">
                {ideasIn.map((i) => (
                  <li key={i.id} className="truncate text-cream-2">
                    {i.emoji && <span className="mr-1">{i.emoji}</span>}
                    {i.text}
                  </li>
                ))}
                {ideasIn.length === 0 && (
                  <li className="text-xs text-cream-3">Empty cluster.</li>
                )}
              </ul>
            </section>
          );
        })}
        {clusters.length === 0 && (
          <div className="panel col-span-full p-8 text-center text-cream-3">
            No clusters yet. Try <em className="text-cream-2 not-italic">Suggest clusters</em>.
          </div>
        )}
      </div>

      {openDetail && (
        <CardDetail
          open={!!openDetail}
          onClose={() => setOpenDetail(null)}
          board={board}
          kind="cluster"
          subjectId={openDetail.id}
          header={
            <span style={{ color: `hsl(${openDetail.colorHue} 65% 70%)` }}>{openDetail.title}</span>
          }
        />
      )}
    </div>
  );
}

// Three-way "how does this theme feel?" picker. The underlying scale
// stays 0-100 for the planner (which weighs themes proportionally),
// but humans pick from three buckets so they're voting on a feeling,
// not negotiating a number.
const FEEL_VALUES = { skip: 0, could: 50, must: 100 } as const;
type FeelKey = keyof typeof FEEL_VALUES;

function weightToFeel(w: number): FeelKey {
  // <25 reads as "skip", 25–74 as "could", ≥75 as "must". The bucket
  // edges match the labels people will say out loud.
  if (w < 25) return 'skip';
  if (w >= 75) return 'must';
  return 'could';
}

function FeelPicker({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (weight: number) => void;
}) {
  const current = weightToFeel(value);
  const opts: Array<{ key: FeelKey; label: string; emoji: string }> = [
    { key: 'skip', label: 'Skip', emoji: '👎' },
    { key: 'could', label: 'Could', emoji: '🤔' },
    { key: 'must', label: 'Must', emoji: '⭐' },
  ];
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-ink-rim/60 bg-ink-2 p-0.5">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(FEEL_VALUES[o.key])}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition ${
            current === o.key
              ? 'bg-clove text-ink'
              : 'text-cream-3 hover:text-cream'
          } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          aria-pressed={current === o.key}
          aria-label={`${o.label} this theme`}
          title={o.label}
        >
          <span aria-hidden>{o.emoji}</span>
          <span className="hidden sm:inline">{o.label}</span>
        </button>
      ))}
    </div>
  );
}
